import { useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'

const TRIGGER_PX = 72 // pull distance that arms a refresh
const MAX_PULL = 110

/**
 * Native-style pull-to-refresh for phones. Listens to touch events at
 * document level: when the page is scrolled to the very top and the user
 * drags down, a spinner indicator descends below the sticky header; pulling
 * past the threshold and releasing runs onRefresh (sync + reload state).
 *
 * Dependency-free and passive — it never calls preventDefault, so native
 * scrolling/rubber-banding is untouched; we only *observe* the gesture.
 * Renders nothing on md+ (mounted, but touch events won't fire on desktop
 * and the indicator is display-hidden there anyway).
 */
export function PullToRefresh({ onRefresh }: { onRefresh: () => Promise<void> }) {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const startY = useRef<number | null>(null)
  const pulling = useRef(false)
  const refreshingRef = useRef(false)

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return
      // Only arm when the page itself is at the very top
      if ((document.scrollingElement?.scrollTop ?? window.scrollY) > 0) return
      // Ignore gestures starting inside dialogs (they scroll internally).
      // e.target can be a text node (no .closest) — resolve to an Element.
      const node = e.target as Node | null
      const el = node instanceof Element ? node : node?.parentElement ?? null
      if (el?.closest('[role="dialog"]')) return
      startY.current = e.touches[0].clientY
      pulling.current = false
    }

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || refreshingRef.current) return
      if ((document.scrollingElement?.scrollTop ?? window.scrollY) > 0) {
        startY.current = null
        setPull(0)
        return
      }
      const delta = e.touches[0].clientY - startY.current
      if (delta > 8) {
        pulling.current = true
        // Rubber-band resistance: display a fraction of the raw pull
        setPull(Math.min(MAX_PULL, delta * 0.45))
      } else if (pulling.current) {
        setPull(0)
      }
    }

    const onTouchEnd = () => {
      if (startY.current === null) return
      const armed = pulling.current && pull >= TRIGGER_PX * 0.45
      startY.current = null
      pulling.current = false
      if (armed && !refreshingRef.current) {
        refreshingRef.current = true
        setRefreshing(true)
        setPull(TRIGGER_PX * 0.45)
        onRefresh()
          .catch(() => { /* refresh errors already surface via sync UI */ })
          .finally(() => {
            refreshingRef.current = false
            setRefreshing(false)
            setPull(0)
          })
      } else {
        setPull(0)
      }
    }

    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
    }
  }, [onRefresh, pull])

  const visible = pull > 4 || refreshing
  const progress = Math.min(1, pull / (TRIGGER_PX * 0.45))

  return (
    <div
      aria-hidden={!refreshing}
      className={cn(
        'pointer-events-none fixed inset-x-0 z-30 flex justify-center md:hidden',
        'top-[calc(env(safe-area-inset-top)+4.5rem)]'
      )}
      style={{
        transform: `translateY(${visible ? pull - 40 : -48}px)`,
        opacity: visible ? Math.max(progress, refreshing ? 1 : 0) : 0,
        transition: pulling.current ? 'none' : 'transform 0.2s ease, opacity 0.2s ease',
      }}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full border bg-card shadow-lg">
        <RefreshCw
          className={cn('h-4 w-4 text-primary', refreshing && 'animate-spin')}
          style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
        />
      </div>
    </div>
  )
}
